import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PartyPopper, SendHorizontal } from 'lucide-react';
import { Avatar, Modal } from '../../components/UI';
import { useAuthStore } from '../../store/authStore';
import { announcementsApi, eventsApi } from '../../services/api';
import MentionText from '../../components/MentionText/MentionText';
import MentionInput from '../../components/MentionInput/MentionInput';
import styles from './Celebrations.module.css';

interface Celebration {
  id: string;
  title: string;
  content: string;
  created_at: string;
  read_count: number;
  author_first_name: string;
  author_last_name: string;
  author_avatar?: string;
  comment_count: number;
  reactions?: any[];
}

interface UpcomingEvent {
  id: string;
  title: string;
  event_date: string;
  event_type: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
}

type FilterType = 'all' | 'birthday' | 'anniversary';

const Celebrations: React.FC = () => {
  const { user, refreshUser } = useAuthStore();
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingEvent[]>([]);
  const [allCelebEvents, setAllCelebEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [timePeriod, setTimePeriod] = useState<'7' | '30' | '90'>('30');
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const highlightWidenTimeRef = useRef(0);

  // Wish (comment) state
  const [wishOpenId, setWishOpenId] = useState<string | null>(null);
  const [wishText, setWishText] = useState('');
  const [wishSubmitting, setWishSubmitting] = useState(false);
  const [wishes, setWishes] = useState<Record<string, any[]>>({});

  // Reactions modal state
  const [showReactionsModal, setShowReactionsModal] = useState(false);
  const [reactionsModalData, setReactionsModalData] = useState<any[]>([]);

  // Add waves state
  const [wavesOpenId, setWavesOpenId] = useState<string | null>(null);
  const [wavesAmount, setWavesAmount] = useState(10);
  const [wavesMessage, setWavesMessage] = useState('');
  const [wavesSubmitting, setWavesSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [timePeriod]);

  // Handle highlight from notification click: scroll to the card and open its
  // wishes; widen the time filter if the item isn't in the current window
  useEffect(() => {
    const id = searchParams.get('highlight');
    if (!id || loading) return;
    const target = celebrations.find(c => c.id === id);
    if (target) {
      // Make sure the card is actually visible: widen the time window if the
      // item is older than it, and reset the birthday/anniversary tab
      const days = Math.floor((Date.now() - new Date(target.created_at).getTime()) / (1000 * 60 * 60 * 24));
      if (days > Number(timePeriod) && timePeriod !== '90') {
        setTimePeriod('90');
        return; // effect re-runs; card will be rendered then
      }
      setFilter('all');
      setHighlightId(id);
      fetchWishes(id);
      setWishOpenId(id);
      setTimeout(() => {
        const el = document.querySelector(`[data-celebration-id="${id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 200);
      setSearchParams({}, { replace: true });
      setTimeout(() => setHighlightId(null), 3000);
    } else if (timePeriod === '7' || timePeriod === '30') {
      highlightWidenTimeRef.current = Date.now();
      setTimePeriod('90');
    } else if (Date.now() - highlightWidenTimeRef.current > 1000) {
      // Widened window has finished loading and the item still isn't there — give up
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [celebrations, loading, searchParams, setSearchParams, timePeriod]);

  const fetchWishes = async (announcementId: string) => {
    if (wishes[announcementId]) return;
    try {
      const res = await announcementsApi.getComments(announcementId);
      setWishes(prev => ({ ...prev, [announcementId]: res.data || [] }));
    } catch { /* ignore */ }
  };

  const handleSubmitWish = async (announcementId: string) => {
    if (wishSubmitting || !wishText.trim()) return;
    setWishSubmitting(true);
    try {
      const res = await announcementsApi.addComment(announcementId, wishText.trim());
      setWishes(prev => ({ ...prev, [announcementId]: [...(prev[announcementId] || []), res.data] }));
      setCelebrations(prev => prev.map(c => c.id === announcementId ? { ...c, comment_count: Number(c.comment_count) + 1 } : c));
      setWishText('');
      setWishOpenId(null);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send wish');
    } finally {
      setWishSubmitting(false);
    }
  };

  const handleAddWaves = async (announcementId: string) => {
    if (wavesSubmitting || !Number.isInteger(wavesAmount) || wavesAmount < 5 || wavesAmount > 100) return;
    setWavesSubmitting(true);
    try {
      await announcementsApi.sendCelebrationWaves(announcementId, {
        points: wavesAmount,
        message: wavesMessage.trim() || undefined,
      });
      setCelebrations(prev => prev.map(c => c.id === announcementId ? { ...c, comment_count: Number(c.comment_count) + 1 } : c));
      const res = await announcementsApi.getComments(announcementId);
      setWishes(prev => ({ ...prev, [announcementId]: res.data || [] }));
      setWavesAmount(10);
      setWavesMessage('');
      setWavesOpenId(null);
      setWishOpenId(announcementId);
      refreshUser();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send waves');
    } finally {
      setWavesSubmitting(false);
    }
  };

  const fetchData = async () => {
    try {
      const now = new Date();
      const targetDate = new Date();
      if (timePeriod === '7') targetDate.setDate(now.getDate() - 7);
      else if (timePeriod === '90') targetDate.setMonth(now.getMonth() - 2);

      const [celebRes, eventsRes] = await Promise.all([
        announcementsApi.getCelebrations({ month: now.getMonth() + 1, year: now.getFullYear() }),
        eventsApi.getByMonth(now.getFullYear(), now.getMonth() + 1).catch(() => ({ data: { events: [] } })),
      ]);
      setCelebrations(celebRes.data.celebrations || []);
      const allEvents = (eventsRes.data.events || []).filter((e: any) =>
        e.event_type === 'birthday' || e.event_type === 'work_anniversary'
      );
      setAllCelebEvents(allEvents);
      const upcomingCeleb = allEvents.filter((e: any) => new Date(e.event_date) > new Date());
      setUpcoming(upcomingCeleb);
    } catch (error) {
      console.error('Failed to fetch celebrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const isBirthday = (title: string) => title.includes('Birthday');
  const isAnniversary = (title: string) => title.includes('Anniversary');

  const timeFiltered = celebrations.filter(c => {
    const days = Math.floor((new Date().getTime() - new Date(c.created_at).getTime()) / (1000 * 60 * 60 * 24));
    return days <= Number(timePeriod);
  });

  const filtered = filter === 'all' ? timeFiltered
    : filter === 'birthday' ? timeFiltered.filter(c => isBirthday(c.title))
    : timeFiltered.filter(c => isAnniversary(c.title));

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1d ago';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const extractName = (title: string) => {
    return title.replace(/^Happy Birthday, |^Happy \d+ Year Work Anniversary, |^Congratulations on \d+ years! - |!/g, '').trim();
  };

  const totalReactions = (c: Celebration) =>
    c.reactions?.reduce((sum: number, r: any) => sum + r.count, 0) || 0;

  const eventsInPeriod = allCelebEvents.filter(e => {
    const days = Math.floor((new Date().getTime() - new Date(e.event_date).getTime()) / (1000 * 60 * 60 * 24));
    return days >= 0 && days <= Number(timePeriod);
  });
  const birthdayCount = eventsInPeriod.filter(e => e.event_type === 'birthday').length;
  const anniversaryCount = eventsInPeriod.filter(e => e.event_type === 'work_anniversary').length;

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <span>Loading celebrations...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1>Celebrations</h1>
          <p>Birthdays and work anniversaries — {filtered.length} in the last {timePeriod} days.</p>
        </div>
        <div className={styles.timeFilterGroup}>
          {(['7', '30', '90'] as const).map(p => (
            <button
              key={p}
              className={`${styles.timeFilterBtn} ${timePeriod === p ? styles.timeActive : ''}`}
              onClick={() => setTimePeriod(p)}
            >
              {p === '7' ? '7 Days' : p === '30' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterRow}>
        <div className={styles.filterGroup}>
          {([
            { key: 'all' as FilterType, label: 'All', dot: '#14201d' },
            { key: 'birthday' as FilterType, label: 'Birthdays', dot: '#d9455f' },
            { key: 'anniversary' as FilterType, label: 'Anniversaries', dot: '#e8a93c' },
          ]).map(f => (
            <button
              key={f.key}
              className={`${styles.filterBtn} ${filter === f.key ? styles.filterActive : ''}`}
              onClick={() => setFilter(f.key)}
            >
              <span className={styles.filterDot} style={{ background: f.dot }} />
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.mainGrid}>
        <div className={styles.cardList}>
          {filtered.map(c => {
            const bday = isBirthday(c.title);
            const color = bday ? '#d9455f' : '#e8a93c';
            const kind = bday ? 'BIRTHDAY' : 'WORK ANNIVERSARY';
            const name = extractName(c.title);
            const reactions = totalReactions(c);
            const celebUser = (c as any).celebrated_user;
            const celebAvatar = celebUser?.avatar_url;
            const celebName = celebUser ? `${celebUser.first_name} ${celebUser.last_name}` : name;

            return (
              <article key={c.id} data-celebration-id={c.id} className={`${styles.card} ${highlightId === c.id ? styles.highlighted : ''}`}>
                <div className={styles.cardHeader}>
                  <Avatar src={c.author_avatar} name={`${c.author_first_name} ${c.author_last_name}`} size="md" />
                  <span className={styles.kindTag} style={{ color, background: `${color}14` }}>{kind}</span>
                  <span className={styles.wavePill}>+1000 waves</span>
                  <div style={{ flex: 1 }} />
                  <span className={styles.cardDate}>{formatTime(c.created_at)}</span>
                </div>

                <div className={styles.stage} style={{ background: `linear-gradient(135deg, ${color}26, #cfe3f5 45%, ${color}14)` }}>
                  <div className={styles.rippleCircle} style={{ borderColor: `${color}4d` }} />
                  <div className={styles.rippleCircle2} style={{ borderColor: `${color}35` }} />
                  <div className={styles.stageInner}>
                    <div className={styles.stageCard}>
                      <Avatar src={celebAvatar} name={celebName} size="lg" />
                      <h3 className={styles.stageHeadline}>{c.title}</h3>
                      <p className={styles.stageDesc}>{c.content.slice(0, 200)}{c.content.length > 200 ? '...' : ''}</p>
                    </div>
                  </div>
                </div>

                <div className={styles.cardActions}>
                  <button className={styles.actionBtn} onClick={() => { fetchWishes(c.id); setWishOpenId(wishOpenId === c.id ? null : c.id); setWavesOpenId(null); }}>
                    Comment ({c.comment_count})
                  </button>
                  {celebUser && celebUser.id !== user?.id && (
                    <button className={styles.actionBtn} onClick={() => { setWavesOpenId(wavesOpenId === c.id ? null : c.id); setWishOpenId(null); }}>
                      Add waves
                    </button>
                  )}
                  <div style={{ flex: 1 }} />
                  {c.reactions && c.reactions.length > 0 && (
                    <div className={styles.reactionsRow} onClick={() => {
                      const allUsers = c.reactions?.flatMap((r: any) => (r.users || []).map((u: any) => ({ ...u, emoji: r.emoji }))) || [];
                      setReactionsModalData(allUsers);
                      setShowReactionsModal(true);
                    }}>
                      {c.reactions.map((r: any) => (
                        <span key={r.emoji} className={styles.reactionChip}>{r.emoji} {r.count}</span>
                      ))}
                    </div>
                  )}
                  <button className={styles.wishBtn} style={{ borderColor: `${color}55`, background: `${color}14`, color }} onClick={() => { fetchWishes(c.id); setWishOpenId(wishOpenId === c.id ? null : c.id); setWavesOpenId(null); }}>
                    Wish them
                  </button>
                </div>

                {/* Add Waves form */}
                {wavesOpenId === c.id && (
                  <div className={styles.wavesForm}>
                    <div className={styles.wavesFormRow}>
                      <label>Waves (5-100)</label>
                      <input type="number" min={5} max={100} value={wavesAmount} onChange={e => setWavesAmount(Number(e.target.value))} className={styles.wavesInput} />
                    </div>
                    <div className={styles.wavesFormRow}>
                      <label>Message (optional)</label>
                      <input type="text" value={wavesMessage} onChange={e => setWavesMessage(e.target.value)} placeholder="Say something nice..." className={styles.wavesInputText} />
                    </div>
                    <div className={styles.wavesFormActions}>
                      <button className={styles.wavesCancel} onClick={() => setWavesOpenId(null)}>Cancel</button>
                      <button className={styles.wavesSubmit} onClick={() => handleAddWaves(c.id)} disabled={wavesSubmitting || wavesAmount < 5 || wavesAmount > 100}>
                        {wavesSubmitting ? 'Adding...' : `Add +${wavesAmount} waves`}
                      </button>
                    </div>
                  </div>
                )}

                {/* Wishes section */}
                {wishOpenId === c.id && (
                  <div className={styles.wishesSection}>
                    {(wishes[c.id] || []).map((w: any) => (
                      <div key={w.id} className={styles.wishRow}>
                        <Avatar src={w.avatar_url} name={`${w.first_name} ${w.last_name}`} size="sm" />
                        <div className={styles.wishContent}>
                          <div className={styles.wishMeta}>
                            <span className={styles.wishAuthor}>{w.first_name} {w.last_name}</span>
                            <span className={styles.wishTime}>{formatTime(w.created_at)}</span>
                          </div>
                          <p className={styles.wishText}>
                            {w.content.startsWith('+') && (
                              <span className={styles.wishWavesPill}>{w.content.split(' ')[0]} {w.content.split(' ')[1]}</span>
                            )}
                            <MentionText text={w.content.startsWith('+') ? w.content.replace(/^\+\d+ waves\s*—?\s*/, '') : w.content} />
                          </p>
                        </div>
                      </div>
                    ))}
                    <div className={styles.wishInputRow}>
                      <Avatar src={user?.avatar_url} name={`${user?.first_name} ${user?.last_name}`} size="sm" />
                      <div className={styles.wishInputContainer}>
                        <MentionInput
                          mode="input"
                          placeholder="Write a wish... Use @ to mention"
                          value={wishText}
                          onChange={setWishText}
                          onSubmit={() => handleSubmitWish(c.id)}
                          disabled={wishSubmitting}
                        />
                        <button
                          className={styles.sendWishBtn}
                          onClick={() => handleSubmitWish(c.id)}
                          disabled={!wishText.trim() || wishSubmitting}
                        >
                          {wishSubmitting ? <div className={styles.tinySpinner}></div> : <SendHorizontal size={16} />}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}

          {filtered.length === 0 && (
            <div className={styles.empty}>
              <PartyPopper size={48} />
              <h3>No celebrations yet this month</h3>
              <p>Upcoming ones are in the sidebar!</p>
            </div>
          )}
        </div>

        <div className={styles.sidebar}>
          <div className={styles.sidebarCard}>
            <div className={styles.sidebarTitle}>Coming up</div>
            <p className={styles.sidebarSubtitle}>Wish them on the day — Ripple will remind you.</p>
            {upcoming.slice(0, 5).map(e => {
              const bday = e.event_type === 'birthday';
              const color = bday ? '#d9455f' : '#e8a93c';
              const name = e.first_name || e.title.replace(/^Happy.*?, /, '').replace('!', '');
              return (
                <div key={e.id} className={styles.upcomingRow}>
                  <Avatar src={e.avatar_url} name={name} size="sm" />
                  <div className={styles.upcomingInfo}>
                    <span className={styles.upcomingName}>{name}</span>
                    <span className={styles.upcomingMeta}>
                      <span className={styles.upcomingDot} style={{ background: color }} />
                      {bday ? 'Birthday' : 'Anniversary'} · {formatDate(e.event_date)}
                    </span>
                  </div>
                </div>
              );
            })}
            {upcoming.length === 0 && (
              <p style={{ fontSize: 13, color: 'rgba(20,32,29,0.4)', textAlign: 'center', padding: 12 }}>No upcoming celebrations</p>
            )}
          </div>

          <div className={styles.monthCard}>
            <span className={styles.monthLabel}>{timePeriod === '7' ? 'THIS WEEK' : timePeriod === '30' ? 'THIS MONTH' : 'LAST 90 DAYS'}</span>
            <h3 className={styles.monthHeadline}>
              {birthdayCount} birthday{birthdayCount !== 1 ? 's' : ''} and {anniversaryCount} {anniversaryCount !== 1 ? 'anniversaries' : 'anniversary'}.
            </h3>
            <p className={styles.monthDesc}>Wishes carry waves too — anything you add lands in their balance.</p>
          </div>
        </div>
      </div>

      <Modal isOpen={showReactionsModal} onClose={() => setShowReactionsModal(false)} title="Reactions">
        <div style={{ minWidth: 280 }}>
          <p style={{ fontSize: 13, color: 'rgba(20,32,29,0.5)', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            {reactionsModalData.length} {reactionsModalData.length === 1 ? 'person' : 'people'} reacted
          </p>
          {reactionsModalData.map((reactor: any, idx: number) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
              <Avatar src={reactor.avatar_url} name={`${reactor.first_name} ${reactor.last_name}`} size="sm" />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#14201d' }}>{reactor.first_name} {reactor.last_name}</div>
              </div>
              <span style={{ fontSize: 16 }}>{reactor.emoji}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default Celebrations;
